import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Star, Play, Image as ImgIcon } from "lucide-react";
import { api, dateTR, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import MediaUploader from "@/components/MediaUploader";
import { SERVICES } from "@/lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const mediaSrc = (url) => (url?.startsWith("http") ? url : `${BACKEND_URL}${url}`);
const emptyForm = { title: "", service_type: "", description: "", media: [], order: 0 };

export default function References() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/references").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEdit(null); setForm(emptyForm); setOpen(true); };
  const startEdit = (r) => {
    setEdit(r.id);
    setForm({ title: r.title || "", service_type: r.service_type || "", description: r.description || "", media: r.media || [], order: r.order || 0 });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Proje / Firma adı zorunlu");
    setBusy(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (edit) await api.put(`/references/${edit}`, payload);
      else await api.post("/references", payload);
      toast.success(edit ? "Güncellendi" : "Referans eklendi");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Referans silinsin mi?")) return;
    await api.delete(`/references/${id}`); load();
  };

  return (
    <div data-testid="admin-references-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#d4af37]">Referanslar</p>
          <h1 className="mt-2 font-display font-extrabold tracking-tighter text-4xl md:text-5xl">Referans & Galeri Yönetimi</h1>
          <p className="text-white/55 text-sm max-w-2xl mt-2">İsim, resim, video veya kombinasyonu yükleyin — sistem dış cephede otomatik olarak doğru görünüme yerleştirir.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button onClick={startNew} data-testid="ref-add" className="inline-flex items-center gap-2 bg-[#d4af37] text-[#121212] hover:bg-white px-5 py-3 font-display uppercase tracking-[0.18em] text-xs transition">
              <Plus className="h-4 w-4" /> Yeni Referans
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border-[#d4af37]/20 text-white max-w-3xl max-h-[90vh] overflow-y-auto kc-scroll">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl tracking-tight">{edit ? "Referans Düzenle" : "Yeni Referans"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Proje / Firma Adı *">
                  <input data-testid="ref-input-title" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} className="kc-input" required />
                </Field>
                <Field label="Hizmet Türü">
                  <select data-testid="ref-input-service" value={form.service_type} onChange={(e)=>setForm({...form,service_type:e.target.value})} className="kc-input">
                    <option value="" className="bg-[#0a0a0a]">— Seçiniz —</option>
                    {SERVICES.map((s) => <option key={s.slug} value={s.title} className="bg-[#0a0a0a]">{s.title}</option>)}
                    <option value="Kurumsal" className="bg-[#0a0a0a]">Kurumsal</option>
                    <option value="Diğer" className="bg-[#0a0a0a]">Diğer</option>
                  </select>
                </Field>
              </div>
              <Field label="Kısa Açıklama">
                <textarea rows={2} value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} className="kc-input resize-none" placeholder="(Opsiyonel) Projenin kısa hikayesi" />
              </Field>
              <Field label="Sıra (Düşük olan önce gösterilir)">
                <input type="number" value={form.order} onChange={(e)=>setForm({...form,order:e.target.value})} className="kc-input w-32" />
              </Field>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45 mb-2">Medya (Resim / Video) — Sürükle & Bırak</div>
                <MediaUploader value={form.media} onChange={(media) => setForm({ ...form, media })} testIdPrefix="ref-media" />
              </div>
              <button type="submit" data-testid="ref-save" disabled={busy} className="w-full bg-[#d4af37] text-[#121212] hover:bg-white disabled:opacity-60 py-3 font-display uppercase tracking-[0.2em] text-xs transition">
                {busy ? "Kaydediliyor..." : (edit ? "Güncelle" : "Kaydet")}
              </button>
            </form>
            <KCStyle />
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.length === 0 ? (
          <div className="md:col-span-2 xl:col-span-3 border border-dashed border-white/10 p-12 text-center text-white/45 font-mono text-xs uppercase tracking-[0.3em]" data-testid="references-empty">
            Henüz referans eklenmedi
          </div>
        ) : items.map((r) => {
          const hasVideo = r.media?.some((m) => m.kind === "video");
          const cover = r.media?.[0];
          return (
            <article key={r.id} className="group border border-white/8 bg-[#121212] hover:border-[#d4af37]/40 transition overflow-hidden" data-testid={`ref-row-${r.id}`}>
              <div className="relative aspect-[16/10] bg-[#0a0a0a] overflow-hidden">
                {cover ? (
                  cover.kind === "video" ? (
                    <video src={mediaSrc(cover.url)} muted className="w-full h-full object-cover" />
                  ) : (
                    <img src={mediaSrc(cover.url)} alt={r.title} className="w-full h-full object-cover" />
                  )
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Star className="h-12 w-12 text-[#d4af37]/40" /></div>
                )}
                {hasVideo && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="h-12 w-12 rounded-full bg-[#d4af37] text-[#121212] flex items-center justify-center"><Play className="h-5 w-5 ml-0.5" fill="currentColor" /></div>
                  </div>
                )}
                <div className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.2em] bg-[#0a0a0a]/80 border border-[#d4af37]/30 text-[#d4af37] px-2 py-1">
                  {r.media?.length > 0 ? (<><ImgIcon className="h-3 w-3" /> {r.media.length} medya</>) : "İsim referansı"}
                </div>
              </div>
              <div className="p-5">
                <div className="font-display text-xl tracking-tight">{r.title}</div>
                {r.service_type && <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#d4af37] mt-1">{r.service_type}</div>}
                {r.description && <div className="text-white/55 text-sm mt-2 line-clamp-2">{r.description}</div>}
                <div className="mt-4 flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/35">{dateTR(r.created_at)}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => startEdit(r)} data-testid={`ref-edit-${r.id}`} className="p-2 text-white/40 hover:text-[#d4af37]"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(r.id)} data-testid={`ref-delete-${r.id}`} className="p-2 text-white/40 hover:text-[#d4af37]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block"><div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45 mb-2">{label}</div>{children}</label>
);
const KCStyle = () => <style>{`.kc-input{width:100%;background:transparent;border:1px solid rgba(255,255,255,0.12);padding:10px 12px;color:white;outline:none}.kc-input:focus{border-color:#d4af37}`}</style>;
